import { defineAbility, type Permission, type PermissionScope, parsePermission, scopeFor } from '@tnn/core'
import { subjects } from './registry'

/**
 * What every signed-in person may do whatever their roles (`loadMe` adds it to theirs): read the branches and the
 * company's details, which is what opens Company to all. The staff directory there is for anyone with an employee
 * record (`/api/company/people`), not a permission
 */
export const everyonePermissions: readonly Permission[] = [
  'company.branch:read',
  'company.profile:read',
  // The company's things in one's own hands: seen, and a lend handed to one confirmed (the server checks it is one's own)
  'company.asset:read@own',
  'company.assetHandover:read@own',
  'company.assetHandover:receive',
  'company.uniform:read@own',
  // An expense paid with one's own money, claimed back (the server takes claims from employees only) and cancelled until paid
  'cash.claim:read@own',
  'cash.claim:create',
  'cash.claim:update@own',
  'cash.claim:delete@own',
  'cash.claim:submit',
  'cash.claim:cancel@own',
  'cash.claim:print',
]

export interface RoleTemplate {
  /** What the database knows it by: renaming one is a migration's (the API's start removes a key the code no longer has) */
  key: string
  name: string
  description: string
  permissions: Permission[]
  /** Keys of the roles whose holders may give this one (`admin.role.grantableBy` by id); a super admin gives any */
  grantableBy: string[]
}

/**
 * Everyone who takes cash holds it (the cash module): sees their own ledger and security deposit,
 * sends collections on to anyone (into a safe: accounting only, `putsIntoSafes`), deposits them, pays petty expenses from their reserve, confirms
 * cash sent to them, returns reserve, and sees accounting's moves of their cash. Cancelling their own documents before
 * the other side confirms is theirs too; the server allows nothing else on other people's cash. A safe's custodian
 * counts cash in and deposits it by being named on the safe, not by a role.
 */
const cashHandler: Permission[] = [
  'cash.entry:read@own',
  'cash.transfer:read@own',
  'cash.transfer:create',
  'cash.transfer:update@own',
  'cash.transfer:delete@own',
  'cash.transfer:submit',
  'cash.transfer:cancel@own',
  'cash.transfer:receive',
  'cash.transfer:print',
  'cash.deposit:read@own',
  'cash.deposit:create',
  'cash.deposit:update@own',
  'cash.deposit:delete@own',
  'cash.deposit:submit',
  'cash.deposit:print',
  'cash.expense:read@own',
  'cash.expense:create',
  'cash.expense:update@own',
  'cash.expense:delete@own',
  'cash.expense:submit',
  'cash.expense:print',
  'cash.reserve:read@own',
  'cash.reserve:create',
  'cash.reserve:update@own',
  'cash.reserve:delete@own',
  'cash.reserve:submit',
  'cash.reserve:cancel@own',
  'cash.reserve:receive',
  'cash.reserve:print',
  // Accounting moved cash into or out of their hands
  'cash.move:read@own',
  // Their own security deposit paid in or used (accounting records it)
  'cash.bond:read@own',
]

/*
 * Someone without a phone tells the department concerned, which records it for them: their time and leave their branch
 * lead (`peopleLead`, `branchClock`), who fills in everyone's OT anyway (ตาราง OT). Cash nobody records for anyone: whoever presses the button holds it,
 * and cash in the wrong hands accounting moves (`cash.move`). Counter staff record nothing for anyone.
 */

/**
 * A branch lead sees and corrects the cash of their branch's people, reviews shortages and surpluses, and approves what
 * their people claim back (accounting transfers it)
 */
const cashLead: Permission[] = [
  'cash.claim:read@branch',
  'cash.claim:approve@branch',
  'cash.claim:cancel@branch',
  'cash.claim:export',
  'cash.transfer:approveVariance@branch',
  'cash.entry:read@branch',
  'cash.entry:export',
  'cash.transfer:read@branch',
  'cash.transfer:cancel@branch',
  'cash.transfer:export',
  'cash.deposit:read@branch',
  'cash.deposit:cancel@branch',
  'cash.deposit:export',
  'cash.expense:read@branch',
  'cash.expense:cancel@branch',
  'cash.expense:export',
  'cash.reserve:read@branch',
]

/**
 * A branch lead looks after their branch's things (the asset register): adds them, hands them to people and takes them
 * back, notes the uniforms given. What a loss costs is accounting's to decide (`review`)
 */
const assetLead: Permission[] = [
  'company.asset:read@branch',
  'company.asset:create@branch',
  'company.asset:update@branch',
  'company.asset:export',
  'company.assetHandover:read@branch',
  'company.assetHandover:create@branch',
  'company.assetHandover:update@branch',
  'company.assetHandover:delete@branch',
  'company.assetHandover:submit@branch',
  'company.assetHandover:cancel@branch',
  'company.assetHandover:amend@branch',
  'company.assetHandover:print',
  'company.assetHandover:export',
  'company.uniform:read@branch',
  'company.uniform:create@branch',
  'company.uniform:update@branch',
  'company.uniform:delete@branch',
  'company.uniform:export',
]

/**
 * A lead brings in their branch's people: invite links (the position's roles), password reset
 * links, approving requests from their branch, and their branch's employees in HR
 */
const peopleLead: Permission[] = [
  'access.invite:read@branch',
  'access.invite:create@branch',
  'access.invite:revoke@branch',
  'access.invite:reset@branch',
  'access.request:read@branch',
  'access.request:approve@branch',
  'hr.employee:read@branch',
  'hr.employee:create@branch',
  'hr.employee:update@branch',
  'hr.department:read',
  'hr.position:read',
  // A job application for someone of their branch without a phone, and those of their branch to look at; HR approves
  'hr.application:read@branch',
  'hr.application:create@branch',
  'hr.application:update@branch',
  // Their people's leave and time: approve, record for someone without a phone, swap days
  'hr.leave:read@branch',
  'hr.leave:create@branch',
  'hr.leave:update@branch',
  'hr.leave:approve@branch',
  'hr.leave:cancel@branch',
  'hr.leave:recordFor@branch',
  'hr.leave:print',
  'hr.leaveAllowance:read@branch',
  // Their people's OT hours, filled in on ตาราง OT
  'hr.overtime:read@branch',
  'hr.overtime:record@branch',
  'hr.overtime:export',
  'hr.timeEntry:read@branch',
  'hr.timeEntry:export',
  'hr.timeEntry:recordFor@branch',
  'hr.timeEntry:approve@branch',
  'hr.schedule:read',
  'hr.holiday:read',
  'hr.leaveType:read',
  // The branch's time-clock screen: a link for a tablet at the branch
  'hr.clockDisplay:read@branch',
  'hr.clockDisplay:create@branch',
  'hr.clockDisplay:revoke@branch',
  // Their people's salary advances (in Payroll): approve, hand over the cash, ask for someone without a phone
  'payroll.advance:read@branch',
  'payroll.advance:approve@branch',
  'payroll.advance:cancel@branch',
  'payroll.advance:pay@branch',
  'payroll.advance:recordFor@branch',
]

/**
 * Dispatch's part of TMS: the jobs, the trips between branches and the delivery runs, the trucks and their crews in full; waybills, receipts, the address book, products and
 * branches to look at — not cancelling another branch's receipt, moving a branch's clock-in spot or clocking people in.
 * Bills go on and off a truck or a run only at their own branch (`scan@branch`), like everyone's: nobody in Bangkok takes
 * bills in at Surat
 */
export const dispatchTms: Permission[] = [
  'tms.shipment:*',
  'tms.trip:read',
  'tms.trip:create',
  'tms.trip:update',
  'tms.trip:delete',
  'tms.trip:export',
  'tms.trip:print',
  'tms.trip:submit',
  'tms.trip:cancel',
  'tms.trip:amend',
  'tms.trip:cost',
  'tms.trip:scan@branch',
  // Every run, but not taking freight at the door: that money goes only into the run's driver's hands
  'tms.deliveryRun:read',
  'tms.deliveryRun:create',
  'tms.deliveryRun:update',
  'tms.deliveryRun:delete',
  'tms.deliveryRun:export',
  'tms.deliveryRun:print',
  'tms.deliveryRun:submit',
  'tms.deliveryRun:cancel',
  'tms.deliveryRun:amend',
  'tms.deliveryRun:scan@branch',
  'tms.deliveryRun:deliver',
  // Made by whoever loads (their `scan`): read, reprinted and cancelled
  'tms.manifest:read',
  'tms.manifest:print',
  'tms.manifest:export',
  'tms.manifest:cancel',
  'tms.vehicle:*',
  'tms.driver:*',
  'tms.waybill:read',
  'tms.waybill:print',
  'tms.waybill:export',
  'tms.receipt:read',
  'tms.contact:read',
  'tms.product:read',
]

/**
 * Moving the bills (เที่ยวรถ, รอบส่ง): a branch opens trips and delivery runs from itself, scans bills on and off trucks
 * where it is (`scan@branch`: at one's home branch), records how a run's deliveries went for a driver without a phone,
 * and hands a bill over to its receiver at the counter. Trips and runs of every branch are there to look at: trucks
 * come in from them
 */
const branchMoves: Permission[] = [
  // The receiver collects a bill with nothing owed at the counter (at the bill's destination only)
  'tms.waybill:handover@branch',
  'tms.trip:read',
  'tms.trip:create',
  'tms.trip:update@branch',
  'tms.trip:delete@branch',
  'tms.trip:submit@branch',
  'tms.trip:scan@branch',
  'tms.trip:print',
  'tms.deliveryRun:read',
  'tms.deliveryRun:create',
  'tms.deliveryRun:update@branch',
  'tms.deliveryRun:delete@branch',
  'tms.deliveryRun:submit@branch',
  'tms.deliveryRun:scan@branch',
  'tms.deliveryRun:deliver@branch',
  'tms.deliveryRun:print',
  'tms.manifest:read',
  'tms.manifest:print',
]

/**
 * A branch lead also cancels and redoes their branch's trips and runs, cancels a ใบคุมรถ their branch made, sends a
 * bill standing at their branch back to its sender (`return@branch`: where it stands is their home branch), and undoes
 * a handover recorded there by mistake (`undeliver@branch`)
 */
const branchMovesLead: Permission[] = [
  'tms.waybill:return@branch',
  'tms.waybill:undeliver@branch',
  'tms.manifest:cancel@branch',
  'tms.manifest:export',
  'tms.trip:cancel@branch',
  'tms.trip:amend@branch',
  'tms.trip:export',
  'tms.deliveryRun:cancel@branch',
  'tms.deliveryRun:amend@branch',
  'tms.deliveryRun:export',
]

/** A branch lead clocks in or out someone of the branch without a phone, standing at the counter */
const branchClock: Permission[] = ['company.branch:clockFor']

/**
 * Who gives which role: a branch lead their counter staff and drivers, HR the others but those who see pay (payroll,
 * the accountant who pays the runs, the manager who reads everything): only a super admin gives those, or HR could
 * give someone sight of pay it may not see itself
 */
const byLead = ['branch_manager', 'hr']
const byHr = ['hr']
const bySuperAdmin: string[] = []

/**
 * Payroll's records that are nobody's pay: the kinds of money, the trip and fuel rates, the settings, and salary advances (what was
 * asked for, decided by branch leads, whom HR appoints). Every other payroll record is someone's pay — what they are
 * paid, extras and deductions, payslips, runs, the income brought forward on a tax declaration — and so is any added later
 */
const notPay = ['payroll.payItem', 'payroll.tripRate', 'payroll.fuelRate', 'payroll.payrollSetting', 'payroll.advance']
const payRecords = subjects.filter((s) => s.app === 'payroll' && !notPay.includes(s.subject))

/** Each action a resource offers that some permissions give, and which of its records it reaches */
export type Rights = Map<string, Map<string, PermissionScope>>

/**
 * What permissions give, resource by resource and action by action: read from the abilities they make (`defineAbility`,
 * as the API enforces them), so wildcards spread over what each resource offers, a narrower grant beside a wider one
 * counts as the wider, and `@own` on a record without an owner reaches all of them, as it does there. The roles page
 * shows a role by it
 */
export function rightsOf(permissions: readonly Permission[]): Rights {
  const probe = { id: 'probe:user', branchId: 'probe:branch' }
  const ability = defineAbility(permissions, probe, subjects)
  const rights: Rights = new Map()
  for (const { subject, actions } of subjects) {
    for (const action of actions) {
      const scope = scopeFor(ability, action, subject)
      if (!scope) continue
      const branch = scope !== 'all' && scope.some((conditions) => Object.values(conditions).includes(probe.branchId))
      rights.set(subject, (rights.get(subject) ?? new Map()).set(action, scope === 'all' ? 'all' : branch ? 'branch' : 'own'))
    }
  }
  return rights
}

/** Whether permissions give everything in every app (`*`): a super admin's */
export const givesEverything = (permissions: readonly Permission[]) =>
  permissions.map(parsePermission).some((p) => p.subject === '*' && p.action === '*' && p.scope === 'all')

/**
 * Whether one may approve a bill, a refund or a pay run they put up or changed themselves: only a super admin, the owner
 * and the one approver, whose own would otherwise wait for nobody. Such an approval is marked (`doc.selfApproved`) and
 * logged as one; everyone else's waits for someone else
 */
export const approvesOwn = (permissions: readonly Permission[]) => givesEverything(permissions)

/** Who may do one action on a resource: everyone signed in (`everyonePermissions`), and each role that gives it, how far */
export interface Access<R> {
  everyone?: PermissionScope
  roles: { role: R; reach: PermissionScope }[]
}

/**
 * Who may do what, asked resource by resource and action by action, from roles as the database or the code has them
 * (each read once with `rightsOf`). Roles that give everything (`givesEverything`) are left out: wherever it is shown,
 * that they may do all of it is said once
 */
export function accessBy<R extends { permissions: readonly Permission[] }>(roles: readonly R[]) {
  const everyone = rightsOf(everyonePermissions)
  const given = roles.filter((role) => !givesEverything(role.permissions)).map((role) => ({ role, rights: rightsOf(role.permissions) }))
  return (subject: string, action: string): Access<R> => ({
    everyone: everyone.get(subject)?.get(action),
    roles: given.flatMap(({ role, rights }) => {
      const reach = rights.get(subject)?.get(action)
      return reach ? [{ role, reach }] : []
    }),
  })
}

/**
 * Whether these rights reach someone else's pay, by any action (reading it, exporting, printing, or changing it, which
 * shows it too). Only one's own records (a payslip `@own`) are not someone else's. A role that does is given only by a
 * super admin (`grantableBy` empty: a test holds every role to it), or whoever gives it could show pay they may not see
 * themselves
 */
export function seesPay(permissions: readonly Permission[]) {
  const rights = rightsOf(permissions)
  return payRecords.some((record) => [...(rights.get(record.subject)?.values() ?? [])].some((reach) => reach !== 'own'))
}

/**
 * Every role there is, and the only place one changes: nobody edits roles or their permissions in the apps (`admin.role`
 * is read only), and each start of the API makes the database match this list (`syncRoles` in apps/api/src/db/seed.ts)
 */
export const defaultRoles: RoleTemplate[] = [
  {
    key: 'super_admin',
    name: 'ผู้ดูแลระบบสูงสุด',
    description: 'ทำได้ทุกอย่างในทุกแอพ',
    permissions: ['*'],
    grantableBy: [],
  },
  {
    key: 'dispatcher',
    name: 'ฝ่ายจัดรถ',
    description: 'สร้างและจ่ายงานขนส่ง ดูแลรถและพนักงานขับรถ ดูบิลงานขนส่ง ใบเสร็จ และสาขาได้',
    // Deposits on charter jobs are taken when booking: payments (accounting.payment) without editing issued ones.
    // The counter's documents (waybills, receipts), branches and the address book are read only: dispatch is the jobs
    permissions: [
      ...dispatchTms,
      ...cashHandler,
      'accounting.customer:read',
      'accounting.payment:read',
      'accounting.payment:create',
      'accounting.payment:submit',
      'accounting.payment:print',
    ],
    grantableBy: byHr,
  },
  {
    key: 'counter',
    name: 'พนักงานหน้าร้าน',
    description: 'รับฝากพัสดุ ออกบิลงานขนส่งและใบเสร็จ ดูแลสมุดผู้ติดต่อ (แก้และเสนอได้เฉพาะบิลของสาขาตัวเอง)',
    permissions: [
      'tms.waybill:read',
      'tms.waybill:create',
      'tms.waybill:update@branch',
      'tms.waybill:delete@branch',
      'tms.waybill:submit@branch',
      'tms.waybill:print',
      'tms.contact:*',
      'tms.product:read',
      'tms.receipt:read',
      'tms.receipt:submit',
      'tms.receipt:print',
      // Asks for a refund of what a customer overpaid, and pays it back once a branch head approves
      'tms.refund:read',
      'tms.refund:create',
      'tms.refund:submit@branch',
      'tms.refund:pay@branch',
      'tms.refund:print',
      ...branchMoves,
      ...cashHandler,
    ],
    grantableBy: byLead,
  },
  {
    key: 'branch_manager',
    name: 'หัวหน้าสาขา',
    description: 'ทุกอย่างของพนักงานหน้าร้าน และยกเลิก แก้ไข แก้ไขเป็นฉบับใหม่ บิลกับใบเสร็จของสาขาตัวเอง',
    permissions: [
      'tms.waybill:read',
      'tms.waybill:create',
      'tms.waybill:update@branch',
      'tms.waybill:delete@branch',
      'tms.waybill:submit@branch',
      'tms.waybill:cancel@branch',
      'tms.waybill:amend@branch',
      'tms.waybill:print',
      'tms.waybill:export',
      'tms.contact:*',
      'tms.product:read',
      'tms.receipt:read',
      'tms.receipt:submit',
      'tms.receipt:print',
      'tms.receipt:export',
      'tms.receipt:update@branch',
      'tms.receipt:cancel@branch',
      'tms.receipt:amend@branch',
      'tms.refund:read',
      'tms.refund:create',
      'tms.refund:submit@branch',
      'tms.refund:approve@branch',
      'tms.refund:pay@branch',
      'tms.refund:cancel@branch',
      'tms.refund:print',
      'tms.refund:export',
      ...branchMoves,
      ...branchMovesLead,
      ...cashHandler,
      ...cashLead,
      ...peopleLead,
      ...assetLead,
      ...branchClock,
    ],
    grantableBy: byHr,
  },
  {
    key: 'driver',
    name: 'พนักงานขับรถ',
    description: 'ดูและอัปเดตสถานะเฉพาะงานของตัวเองผ่านมือถือ ส่งของและเก็บเงินในรอบส่งของตัวเอง',
    // Drivers collect freight at the door and pay for fuel and tolls: cash in hand. Their own runs: delivering, and
    // taking the freight on the branch's receipt — no receipt rights of their own (`collect@own` reaches only bills out on
    // their run, the cash in their hands). Trips between branches are the branches' to scan: their drivers use no app
    permissions: [
      'tms.shipment:read@own',
      'tms.shipment:update@own',
      'tms.deliveryRun:read@own',
      'tms.deliveryRun:deliver@own',
      'tms.deliveryRun:collect@own',
      'tms.vehicle:read',
      ...cashHandler,
    ],
    grantableBy: byLead,
  },
  {
    key: 'accountant',
    name: 'ฝ่ายบัญชี',
    description: 'ลูกค้า ใบแจ้งหนี้ และรายงานลูกหนี้ ดูงานขนส่งเพื่อวางบิล รับเงิน แก้และยกเลิกใบเสร็จได้ทุกสาขา',
    permissions: [
      // Everything in accounting but approving bills: a manager approves what accounting records (and then pays)
      'accounting.customer:*',
      'accounting.invoice:*',
      'accounting.billingNote:*',
      'accounting.creditNote:*',
      // A receipt in the customer's name in place of a counter one: accounting's alone, not the counter's or a lead's
      'accounting.customerReceipt:*',
      'accounting.payment:*',
      'accounting.account:*',
      'accounting.billPayment:*',
      'accounting.bill:read',
      'accounting.bill:create',
      'accounting.bill:update',
      'accounting.bill:delete',
      'accounting.bill:export',
      'accounting.bill:print',
      'accounting.bill:submit',
      'accounting.bill:cancel',
      'accounting.bill:amend',
      'tms.shipment:read',
      'tms.shipment:export',
      'tms.waybill:read',
      'tms.waybill:print',
      'tms.receipt:*',
      'tms.refund:*',
      // Every person's cash: issues and tops up reserves, reviews collections spent, verifies deposits, sets up the
      // safes (at which branch) and moves cash between accounts; approves expense claims and transfers them back
      'cash.*',
      // What the company's things are worth, and whether a damaged or lost one comes out of the person's deposit
      'company.asset:read',
      'company.asset:export',
      'company.assetHandover:read',
      'company.assetHandover:review',
      'company.assetHandover:print',
      'company.assetHandover:export',
      // Pays the approved runs and files their tax and social security
      'payroll.payrollRun:read',
      'payroll.payrollRun:export',
      'payroll.payrollRun:pay',
      'payroll.payslip:read',
      'payroll.payslip:export',
      'payroll.payslip:print',
      // Transfers the approved advances
      'payroll.advance:read',
      'payroll.advance:pay',
      'payroll.advance:export',
    ],
    grantableBy: bySuperAdmin,
  },
  {
    key: 'manager',
    name: 'ผู้จัดการ',
    description: 'ดู ส่งออก และพิมพ์ได้ทุกแอพ เสนอใบแจ้งหนี้',
    // Approves the pay runs payroll prepares (and reopens one before it is paid), and the bills accounting records
    permissions: [
      '*:read',
      '*:export',
      '*:print',
      'accounting.invoice:submit',
      'payroll.payrollRun:approve',
      'payroll.payrollRun:reopen',
      'accounting.bill:approve',
      'tms.refund:approve',
    ],
    grantableBy: bySuperAdmin,
  },
  {
    key: 'hr',
    name: 'ฝ่ายบุคคล (HR)',
    description:
      'พนักงาน แผนก ตำแหน่ง ข้อมูลส่วนตัว เวลาทำงาน ลิงก์เชิญ และอนุมัติคำขอทุกสาขา (ให้บทบาทอื่นได้ ยกเว้นผู้ดูแลระบบและบทบาทที่เห็นเงินเดือน: ฝ่ายเงินเดือน ฝ่ายบัญชี ผู้จัดการ)',
    // Often held together with another role (the accountant does HR): a user may have several. Pay is not HR's (Payroll)
    permissions: [
      'hr.*',
      'access.*',
      'company.branch:rotateCode',
      'tms.driver:read',
      // Every branch's things: the register, handing them out and taking them back (a loss's cost is accounting's)
      'company.asset:*',
      'company.assetHandover:read',
      'company.assetHandover:create',
      'company.assetHandover:update',
      'company.assetHandover:delete',
      'company.assetHandover:submit',
      'company.assetHandover:cancel',
      'company.assetHandover:amend',
      'company.assetHandover:print',
      'company.assetHandover:export',
      'company.uniform:*',
    ],
    grantableBy: [],
  },
  {
    key: 'payroll',
    name: 'ฝ่ายเงินเดือน',
    description: 'ค่าจ้าง งวดเงินเดือนและสลิป เบิกเงินล่วงหน้า ภาษีและประกันสังคม (ให้บทบาทนี้ได้เฉพาะผู้ดูแลระบบ)',
    // Pay is kept from HR: only a super admin gives this role. HR's records it works from are read only: who is
    // employed and where, and their time, leave and overtime to check a payslip against
    permissions: [
      'payroll.*',
      'hr.employee:read',
      'hr.department:read',
      'hr.position:read',
      'hr.timeEntry:read',
      'hr.timeEntry:export',
      'hr.leave:read',
      'hr.overtime:read',
      'hr.schedule:read',
      'hr.holiday:read',
      'hr.leaveType:read',
    ],
    grantableBy: bySuperAdmin,
  },
]
